//@flow

// Vendors
import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Texkit Components
import { ButtonPrimary } from 'texkit/dist/components/Buttons';

// Components
import Modal from '../Modal';
import Trans from '../../Trans/Trans';

// Actions
import { closeModal } from '../../../../reducers/modal/modalActions';

// Hooks
import useDLIDBarcodeScanner, {
  CAMERA_ACCESS_DENIED,
} from '../../../../hooks/useDLIDBarcodeScanner';

// Helpers
import {
  BARCODE_SCAN_EVENTS,
  trackBarcodeScanEvent,
} from '../../../../lib/helpers/barcodeScanAnalytics';

// Assets
import alertWarningIcon from '../../../../assets/icons/alert-triangle.svg';
import xCircleIcon from '../../../../assets/icons/x-circle.svg';
import { ReactComponent as DriverLicense } from '../../../../assets/icons/DriverLicense.svg';

// Styles
import './dl-id-barcode-scan-modal.scss';

type Props = {
  onCapture?: Function,
  onClose?: Function,
};

const DLIDBarcodeScanModal = ({ onCapture, onClose }: Props) => {
  const dispatch = useDispatch();
  const isOpen = useSelector(
    ({ modal }) => modal.activeModal === 'DLIDBarcodeScanModal'
  );
  const pathname = useSelector(({ router }) => router.location.pathname);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  // Redux keeps the modal open across navigation (e.g. browser back), so close it when leaving.
  useEffect(
    () => () => {
      if (!isOpenRef.current) return;
      dispatch(closeModal('DLIDBarcodeScanModal'));
      if (document.body) document.body.classList.remove('modalActive');
    },
    [dispatch, pathname]
  );
  const openEventIdRef = useRef(null);
  useEffect(() => {
    if (!isOpen) {
      openEventIdRef.current = null;
      return;
    }
    if (openEventIdRef.current) return;
    try {
      openEventIdRef.current = window.crypto.randomUUID();
    } catch {
      return;
    }
    dispatch(
      trackBarcodeScanEvent(BARCODE_SCAN_EVENTS.MODAL_OPENED)
    );
  }, [dispatch, isOpen]);
  const handleCapture = useCallback(
    capturedId => {
      if (onCapture) onCapture(capturedId);
      dispatch(closeModal('DLIDBarcodeScanModal'));
    },
    [dispatch, onCapture]
  );
  const {
    scannerElementRef,
    scannerError,
    loadError,
    isLoading,
    retry,
    reload,
  } = useDLIDBarcodeScanner({ enabled: isOpen, onCapture: handleCapture });

  const handleManualEntry = () => {
    dispatch(closeModal('DLIDBarcodeScanModal'));
  };

  const renderErrorState = (error, onRetry, isLoadError = false) => {
    // Helper to check if value is a translation key object
    const isTransKey = (value) => value && typeof value === 'object' && value.file && value.id;
    
    // Helper to render error text (handles both Trans keys and strings)
    const renderErrorText = (value, fallback) => {
      if (isTransKey(value)) {
        return <Trans file={value.file} id={value.id} fallback={fallback} />;
      }
      if (typeof value === 'string') {
        return value;
      }
      return null;
    };

    const isCameraAccessError = error?.code === CAMERA_ACCESS_DENIED;
    const titleKey = isCameraAccessError
      ? { file: 'Errors', id: 'CameraAccessNeededTitle' }
      : error?.title;
    const messageKey = isCameraAccessError
      ? { file: 'Errors', id: 'CameraAccessNeeded' }
      : error?.message;
    const titleFallback = isCameraAccessError
      ? 'We can’t access your camera'
      : undefined;
    const messageFallback = isCameraAccessError
      ? 'Close this window to enter your information manually and continue without scanning. Or, to scan your driver license or ID, first allow camera access for this site in your browser or device settings. You may have to start over.'
      : undefined;

    // Extract title (prioritize error.title, fall back to error.message)
    const title =
      renderErrorText(titleKey, titleFallback) ||
      renderErrorText(messageKey, messageFallback) ||
      'Barcode not found';

    // Extract message only if we have a title and a separate message
    const message = titleKey && renderErrorText(messageKey, messageFallback);

    // Use x-circle icon for load errors, alert-warning for scanner errors
    const showErrorIcon = isLoadError && !isCameraAccessError;
    const iconSrc = showErrorIcon ? xCircleIcon : alertWarningIcon;
    const iconAlt = showErrorIcon ? 'Error' : 'Warning';

    return (
      <div className="dl-id-barcode-scan-preview-error" role="alert">
        <img
          src={iconSrc}
          alt={iconAlt}
          className="dl-id-barcode-scan-preview-error-icon"
        />
        <h3 className="dl-id-barcode-scan-preview-error-title">{title}</h3>
        {!message && <div className="dl-id-barcode-scan-padding"/>}
        {message && (
          <p className="dl-id-barcode-scan-preview-error-message">{message}</p>
        )}
        {!isCameraAccessError && (
          <ButtonPrimary
            type="button"
            className="dl-id-barcode-preview-retry-button"
            onClick={onRetry}
          >
            <Trans file="Labels" id="TryAgain" fallback="Try again" />
          </ButtonPrimary>
        )}
      </div>
    );
  };

  return (
    <Modal
      name="DLIDBarcodeScanModal"
      className="dl-id-barcode-scan-modal"
      onClose={onClose}
    >
      <div
        className={`dl-id-barcode-scan-preview-wrapper ${
          scannerError || loadError ? 'has-error' : ''
        }`}
      >
        <div
          ref={scannerElementRef}
          className="dl-id-barcode-scan-preview"
          aria-label="Driver license barcode scanner"
        />
        {isLoading && !loadError && !scannerError && (
          <div
            className="dl-id-barcode-scan-preview-loader"
            role="status"
            aria-live="polite"
            aria-label="Loading scanner"
          >
            <span className="dl-id-barcode-scan-preview-loader-dot" />
            <span className="dl-id-barcode-scan-preview-loader-dot" />
            <span className="dl-id-barcode-scan-preview-loader-dot" />
          </div>
        )}
        {loadError && renderErrorState(loadError, reload, true)}
        {scannerError && renderErrorState(scannerError, retry, false)}
      </div>
      <div className="dl-id-barcode-scan-instructions">
        <h2>
          <Trans
            file="Labels"
            id="ScanBarcodeOnDLID"
            fallback="Scan the barcode on the back of your driver license or ID"
          />
        </h2>
        <p>
          <Trans
            file="Labels"
            id="PositionBarcodeInsideFrame"
            fallback="Position the code inside the frame."
          />
        </p>
        <DriverLicense
          className="dl-id-barcode-example"
          aria-hidden="true"
        />
        <button
          type="button"
          className="text-button dl-id-barcode-manual-entry"
          onClick={handleManualEntry}
        >
          <Trans
            file="Labels"
            id="EnterInformationManually"
            fallback="Enter information manually"
          />
        </button>
      </div>
    </Modal>
  );
};

export default DLIDBarcodeScanModal;
